import { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { http, errorText } from "@/lib/adminApi";

const field = "w-full rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-white placeholder:text-slate-500 focus:border-[var(--teal)] focus:outline-none";
const EMPTY = { subject: "", message: "", promo_code: "" };

const History = ({ campaigns }) => (
  <ul className="divide-y divide-white/10 rounded-2xl border border-white/10" data-testid="offer-history">
    {campaigns.map((c) => (
      <li key={c.id} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-sm">
        <span className="text-white">{c.subject}{c.promo_code ? ` · ${c.promo_code}` : ""}</span>
        <span className="text-slate-400">{c.created_at?.slice(0, 10)} · {c.status === "done" ? "Sent" : "Sending"} {c.sent}/{c.audience}{c.failed ? ` · ${c.failed} failed` : ""}</span>
      </li>
    ))}
    {campaigns.length === 0 && <li className="px-5 py-4 text-sm text-slate-300">No offers sent yet.</li>}
  </ul>
);

export const OfferSender = () => {
  const [data, setData] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [stage, setStage] = useState("edit");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => http.get("/admin/offers").then((r) => setData(r.data || { audience: 0, campaigns: [] })).catch(() => setData({ audience: 0, campaigns: [] }));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setStage("edit"); };
  const run = async (fn) => { setBusy(true); setMsg(""); try { await fn(); } catch (err) { setMsg(errorText(err)); } setBusy(false); };

  const test = () => run(async () => { const { data: r } = await http.post("/admin/offers/test", f); setStage("tested"); setMsg(`Test sent to ${r?.to}. Check it, then send to everyone.`); });
  const send = () => run(async () => {
    await http.post("/admin/offers/send", { ...f, confirm_count: data?.audience ?? 0 });
    setF(EMPTY); setStage("edit"); setMsg("Sending now. Progress shows below."); load();
  });
  if (!data) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-[var(--teal)]" />;
  const audience = data.audience ?? 0;

  return (
    <div className="mt-10 space-y-6" data-testid="admin-offers-panel">
      <p className="text-slate-200"><span className="font-serif-d text-4xl text-white" data-testid="offer-audience-count">{audience}</span> subscribers opted in to offers. Each email includes an unsubscribe link.</p>
      <div className="space-y-3 rounded-2xl border border-white/10 bg-[var(--navy)] p-5 sm:p-6" data-testid="offer-form">
        <input className={field} placeholder="Subject, e.g. Launch week: 20% off your resume rewrite" value={f.subject} onChange={set("subject")} data-testid="offer-subject-input" />
        <textarea rows={7} className={field} placeholder={"Your message. Leave a blank line between paragraphs."} value={f.message} onChange={set("message")} data-testid="offer-message-input" />
        <input className={`${field} uppercase`} placeholder="Promo code (optional, must exist in Promo codes)" value={f.promo_code} onChange={set("promo_code")} data-testid="offer-promo-input" />
        {msg && <p className="text-sm text-[var(--teal)]" role="status" data-testid="offer-message">{msg}</p>}
        <div className="flex flex-wrap gap-2">
          <button onClick={test} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--teal)] disabled:opacity-60" data-testid="offer-test-button">
            {busy && stage === "edit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send me a test
          </button>
          {stage === "tested" && (
            <button onClick={() => setStage("confirm")} disabled={!audience} className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] disabled:opacity-40" data-testid="offer-send-button">
              <Send className="h-4 w-4" /> Send to {audience} subscribers
            </button>
          )}
        </div>
        {stage === "confirm" && (
          <div className="rounded-xl border border-[var(--amber)]/50 p-4" data-testid="offer-confirm">
            <p className="text-sm text-white">Send “{f.subject}” to {audience} real people now? This can't be undone.</p>
            <div className="mt-3 flex gap-2">
              <button onClick={send} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-5 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60" data-testid="offer-confirm-button">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Yes, send it
              </button>
              <button onClick={() => setStage("tested")} className="rounded-full border border-white/20 px-4 py-2 text-sm text-slate-300" data-testid="offer-cancel-button">Cancel</button>
            </div>
          </div>
        )}
      </div>
      <History campaigns={Array.isArray(data.campaigns) ? data.campaigns : []} />
    </div>
  );
};
